import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectChart,
  selectDate,
  selectRuntime,
  setChart,
  setDate,
  setRuntime,
} from 'redux/reducers/synopticChartsReducer';
import { Chart, Runtime } from 'helper/products/synopticCharts';

interface UseInitialLoadParams {
  monitoringOptions?: Array<{ label: string; charts: Chart[] }>;
  runtimes?: Runtime[];
  isLoading: boolean;
}

const getLatestDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DEFAULT_VALUES = {
  dateString: getLatestDateString(),
  chartId: 26,
  runtimeId: '0',
};

export default function useInitialLoad({
  monitoringOptions,
  runtimes,
  isLoading,
}: UseInitialLoadParams) {
  const dispatch = useDispatch();
  const hasInitialized = useRef(false);

  const currentChart = useSelector(selectChart);
  const currentDate = useSelector(selectDate);
  const currentRuntime = useSelector(selectRuntime);

  useEffect(() => {
    if (isLoading || hasInitialized.current || !monitoringOptions || !runtimes) {
      return;
    }

    const hasExistingState = currentChart || currentDate || currentRuntime;

    if (!hasExistingState) {
      const defaultChart = monitoringOptions
        .flatMap((monitoring) => monitoring.charts)
        .find((chart) => chart.id === DEFAULT_VALUES.chartId);

      const defaultRuntime = runtimes.find((runtime) => runtime.id === DEFAULT_VALUES.runtimeId);

      if (defaultChart) {
        dispatch(setChart(defaultChart));
      }

      dispatch(setDate(DEFAULT_VALUES.dateString));

      if (defaultRuntime) {
        dispatch(setRuntime(defaultRuntime));
      }
    }

    hasInitialized.current = true;
  }, [monitoringOptions, runtimes, isLoading, currentChart, currentDate, currentRuntime, dispatch]);
}
